import { useCallback, useMemo } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import type { DraftPair } from "../../challenges/types/challengeDraft";
import {
  calculatePocoStandings,
  getPocoCategoryRoundIndex,
  getPocoRoundsCount,
  getPocoRoundCategoryKey,
  rebuildPocoRoundsForCategory,
  type AdminRoundMatch,
} from "../../challenges/utils/pocoSchedule";
import { parseRoundsInput } from "../utils/adminChallengeEditor";

type UseAdminChallengeRoundsInput = {
  challengeDetail: ChallengeDetail | null;
  challengeRoundsJson: string;
  draftPairs: DraftPair[];
  isPoco: boolean;
  onCompleteScore?: (rounds: any[][]) => void;
  setChallengeRoundsJson: (value: string) => void;
};

function hasScoreValue(value: unknown) {
  if (value === "" || value === null || value === undefined) return false;
  return Number.isFinite(Number(value));
}

function hasCompleteScore(match: any) {
  return hasScoreValue(match?.score1) && hasScoreValue(match?.score2);
}

export function useAdminChallengeRounds({
  challengeDetail,
  challengeRoundsJson,
  draftPairs,
  isPoco,
  onCompleteScore,
  setChallengeRoundsJson,
}: UseAdminChallengeRoundsInput) {
  const pairLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    draftPairs.forEach((pair) => {
      map[pair.id] = pair.label;
    });
    (challengeDetail?.pairs || []).forEach((pair) => {
      if (!map[pair.id]) map[pair.id] = pair.label;
    });
    return map;
  }, [challengeDetail?.pairs, draftPairs]);

  const resolveRoundPairLabel = useCallback(
    (pairId: string) => {
      const safeId = String(pairId || "").trim();
      if (!safeId) return siteTexts.challengeEngine.labels.removedPair;
      return (
        pairLabelById[safeId] || siteTexts.challengeEngine.labels.removedPair
      );
    },
    [pairLabelById],
  );

  const roundsParsed = useMemo(
    () => parseRoundsInput(challengeRoundsJson) || [],
    [challengeRoundsJson],
  );

  const pocoPointsByPairId = useMemo(() => {
    if (!isPoco) return {} as Record<string, number>;
    const roundsByCategory = (roundsParsed as AdminRoundMatch[][]).reduce<
      Record<string, AdminRoundMatch[][]>
    >((acc, round) => {
      const categoryKey = getPocoRoundCategoryKey(round);
      if (!acc[categoryKey]) acc[categoryKey] = [];
      acc[categoryKey].push(round);
      return acc;
    }, {});
    return Object.entries(roundsByCategory).reduce<Record<string, number>>(
      (pointsByPair, [categoryKey, categoryRounds]) => {
        const finalRoundIndex = categoryRounds.length - 1;
        const standings = calculatePocoStandings(
          categoryRounds.slice(0, finalRoundIndex),
          {
            includeRoundIndexes: Array.from(
              { length: Math.max(0, finalRoundIndex - 1) },
              (_item, index) => index + 1,
            ),
          },
        );
        standings.forEach((row) => {
          pointsByPair[`${categoryKey}\u0000${row.id}`] = row.points;
        });
        return pointsByPair;
      },
      {},
    );
  }, [isPoco, roundsParsed]);

  const scoreMax =
    challengeDetail?.challengeModel === "custom" &&
    (challengeDetail.gameType || "games") === "games" &&
    Number(challengeDetail.gameCount || 0) > 0
      ? Math.floor(Number(challengeDetail.gameCount))
      : challengeDetail?.challengeType === "super8"
        ? 4
        : 5;

  const resolveRoundPairLabelWithPoints = useCallback(
    (pairId: string, roundIndex: number) => {
      const baseLabel = resolveRoundPairLabel(pairId);
      const pocoRounds = roundsParsed as AdminRoundMatch[][];
      const categoryRoundIndex = getPocoCategoryRoundIndex(
        pocoRounds,
        roundIndex,
      );
      const categoryKey = getPocoRoundCategoryKey(pocoRounds[roundIndex]);
      const firstCategoryRound = pocoRounds.find(
        (round) => getPocoRoundCategoryKey(round) === categoryKey,
      );
      const categoryRoundsCount = Math.max(
        getPocoRoundsCount(firstCategoryRound?.length),
        pocoRounds.filter(round => getPocoRoundCategoryKey(round) === categoryKey).length,
      );
      if (!isPoco || categoryRoundIndex !== categoryRoundsCount - 1) {
        return baseLabel;
      }
      const safeId = String(pairId || "").trim();
      if (!safeId) return baseLabel;
      const points = Number(pocoPointsByPairId[`${categoryKey}\u0000${safeId}`]);
      if (!Number.isFinite(points)) return baseLabel;
      return `${baseLabel} (${points} pts)`;
    },
    [isPoco, pocoPointsByPairId, resolveRoundPairLabel, roundsParsed],
  );

  const updateRoundScore = useCallback(
    (
      roundIndex: number,
      matchIndex: number,
      field: "score1" | "score2",
      value: string,
    ) => {
      const next = roundsParsed.map((round: any[], rIndex: number) => {
        if (rIndex !== roundIndex) return round;
        return round.map((match: any, mIndex: number) => {
          if (mIndex !== matchIndex) return match;
          if (value === "") return { ...match, [field]: "" };
          let numeric = Number(value);
          if (!Number.isFinite(numeric)) return { ...match, [field]: "" };
          if (numeric > scoreMax) numeric = scoreMax;
          if (numeric < 0) numeric = 0;
          const updated = { ...match, [field]: numeric };
          return hasCompleteScore(updated)
            ? { ...updated, status: "finished" }
            : updated;
        });
      });
      setChallengeRoundsJson(JSON.stringify(next, null, 2));
      const updatedMatch = next[roundIndex]?.[matchIndex];
      if (hasCompleteScore(updatedMatch)) {
        onCompleteScore?.(next);
      }
    },
    [onCompleteScore, roundsParsed, scoreMax, setChallengeRoundsJson],
  );

  const updatePocoWinner = useCallback(
    (
      roundIndex: number,
      matchIndex: number,
      winnerSide: "dupla1" | "dupla2" | "",
    ) => {
      const next = roundsParsed.map((round: any[]) =>
        Array.isArray(round) ? round.map((match: any) => ({ ...match })) : [],
      );
      const round = next[roundIndex];
      const match = round?.[matchIndex];
      if (!match) return;

      if (winnerSide === "dupla1") {
        match.score1 = 1;
        match.score2 = 0;
      } else if (winnerSide === "dupla2") {
        match.score1 = 0;
        match.score2 = 1;
      } else {
        match.score1 = "";
        match.score2 = "";
      }

      const rebuilt = rebuildPocoRoundsForCategory(
        next as AdminRoundMatch[][],
        roundIndex,
      );
      setChallengeRoundsJson(JSON.stringify(rebuilt, null, 2));
    },
    [roundsParsed, setChallengeRoundsJson],
  );

  const applyQuickWinnerForRoundMatch = useCallback(
    (
      roundIndex: number,
      matchIndex: number,
      winnerSide: "dupla1" | "dupla2" | "",
    ) => {
      if (isPoco) {
        updatePocoWinner(roundIndex, matchIndex, winnerSide);
        return;
      }
      const winnerScore = Math.max(1, Number(scoreMax || 0));
      const loserScore = Math.max(0, winnerScore - 1);
      const next = roundsParsed.map((round: any[]) =>
        Array.isArray(round) ? round.map((match: any) => ({ ...match })) : [],
      );
      const round = next[roundIndex];
      const match = round?.[matchIndex];
      if (!match) return;
      if (winnerSide === "dupla1") {
        match.score1 = winnerScore;
        match.score2 = loserScore;
      } else if (winnerSide === "dupla2") {
        match.score1 = loserScore;
        match.score2 = winnerScore;
      } else {
        match.score1 = "";
        match.score2 = "";
      }
      setChallengeRoundsJson(JSON.stringify(next, null, 2));
    },
    [isPoco, roundsParsed, scoreMax, setChallengeRoundsJson, updatePocoWinner],
  );

  return {
    applyQuickWinnerForRoundMatch,
    pairLabelById,
    resolveRoundPairLabelWithPoints,
    roundsParsed,
    scoreMax,
    updatePocoWinner,
    updateRoundScore,
  };
}
